import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";

// Contexts & Utils
import { useVoyageContextV2 } from "../../Contexts/VoyageContextV2";
import { useUserContext } from "../../Contexts/UserContext";
import { useCompanyContext } from "../../Contexts/CompanyContext";
import { useSettings } from "../../Contexts/SettingsContext";
import { activitiesLoading } from "./constants/loadingactivities";
import { activitiesDischarging } from "./constants/dischargingActivities";
import { getLabel } from "./constants/getLabel";
import { calculateCargoFigures } from "./utils/cargoCalculations";
import { getSelectedCargo, getSelectedTrip } from "../../utils/voyageSelection";
import { getVoyageNumber } from "../../utils/domainSelectors";

// Komponenter
import ActivityLogTable from "./ActivityLogTable";
import { renderSofV2 } from "../../print-v2";
import VoyageInfoTable from "./VoyageInformationTable";
import "../../styles/StatementOfFact.css";

const hasMeaningfulValue = (value) => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return !Number.isNaN(value);
  if (typeof value === "boolean") return value;
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (typeof value === "object") {
    return Object.values(value).some(hasMeaningfulValue);
  }
  return false;
};

const isActivityEmpty = (activityData) => !hasMeaningfulValue(activityData);

const StatementOfFact = () => {
  const { t } = useTranslation("statementoffact");
  const theme = useTheme();

  const {
    currentVoyage,
    isVoyageReadOnly,
    selectedTripId,
    selectedCargoIndex,
    updateTripCargo,
    addLog,
  } = useVoyageContextV2();

  const { user } = useUserContext();
  const { timezone, is24Hour } = useSettings();
  const { companyLogo } = useCompanyContext();

  const [cargoActivities, setCargoActivities] = useState({});
  const [editableActivities, setEditableActivities] = useState({});
  const [loading, setLoading] = useState(true);
  const [activitiesSchema, setActivitiesSchema] = useState(activitiesLoading);

  const [shipFigure, setShipFigure] = useState();
  const [blFigure, setBlFigure] = useState();
  const [percentDiff, setPercentDiff] = useState();
  const [figureDiff, setFigureDiff] = useState();
  const previousCargoIdRef = useRef(null);
  const cargoFigureCalculationTimeoutRef = useRef(null);
  const selectedTrip = getSelectedTrip(currentVoyage, selectedTripId);
  const selectedCargo = getSelectedCargo(
    currentVoyage,
    selectedTripId,
    selectedCargoIndex
  );

  // 2. Hantera val av schema (Loading vs Discharging)
  useEffect(() => {
    if (selectedTrip) {
      setActivitiesSchema(
        selectedTrip.operationType === "Discharging"
          ? activitiesDischarging
          : activitiesLoading
      );
    }
  }, [selectedTrip]);

  const handleActivityChange = useCallback((activity, field, value) => {
    if (field === "remarks.shipfig") setShipFigure(value);
    if (field === "remarks.blfig") setBlFigure(value);

    setCargoActivities((prev) => {
      const updated = { ...prev };
      if (activity === "generalRemarks") {
        updated.generalRemarks = { ...updated.generalRemarks, remarks: value };
      } else if (field.startsWith("remarks.")) {
        const remarkKey = field.split(".")[1];
        updated[activity] = {
          ...updated[activity],
          remarks: {
            ...(updated[activity]?.remarks || {}),
            [remarkKey]: value,
          },
        };
      } else {
        updated[activity] = { ...updated[activity], [field]: value };
      }
      return updated;
    });
  }, []);

  /**
   * 3. Synka data från Context till lokal state.
   * Körs när resa, trip eller cargo ändras.
   */
  // Inuti StatementOfFact.js
  useEffect(() => {
    if (selectedCargo) {
      const selectedCargoId = selectedCargo.cargoId || null;
      const didCargoChange = previousCargoIdRef.current !== selectedCargoId;
      const isAnyEditing = Object.values(editableActivities).some(
        (v) => v === true
      );

      // Vid faktiskt cargo-byte måste vi alltid byta hela arbetsstaten.
      if (didCargoChange || !isAnyEditing) {
        const currentData = selectedCargo.activities || {};
        setCargoActivities((prev) => (prev === currentData ? prev : currentData));

        const initialMap = {};
        Object.keys(activitiesSchema).forEach((key) => {
          initialMap[key] = isActivityEmpty(currentData[key]);
        });

        const sameKeys =
          Object.keys(editableActivities).length ===
          Object.keys(initialMap).length;
        const sameValues =
          sameKeys &&
          Object.keys(initialMap).every(
            (key) => editableActivities[key] === initialMap[key]
          );
        if (!sameValues) {
          setEditableActivities(initialMap);
        }
        previousCargoIdRef.current = selectedCargoId;
        setLoading(false);
      }
    }
  }, [
    activitiesSchema,
    editableActivities,
    selectedCargo,
  ]);

  // 4. Automatiska beräkningar för Cargo Figures
  useEffect(() => {
    if (cargoFigureCalculationTimeoutRef.current) {
      clearTimeout(cargoFigureCalculationTimeoutRef.current);
      cargoFigureCalculationTimeoutRef.current = null;
    }

    if (!shipFigure || !blFigure || !selectedTrip) {
      return undefined;
    }

    cargoFigureCalculationTimeoutRef.current = setTimeout(() => {
      const { diff, percent, date, time } = calculateCargoFigures(
        shipFigure,
        blFigure
      );
      setPercentDiff(percent);
      setFigureDiff(diff);

      const targetKey =
        selectedTrip.operationType === "Loading"
          ? "calculations-done"
          : "calculations";

      handleActivityChange(targetKey, "date", date);
      handleActivityChange(targetKey, "time", time);
      handleActivityChange(targetKey, "remarks.diff", diff);
      handleActivityChange(targetKey, "remarks.percent", percent);
    }, 300);

    return () => {
      if (cargoFigureCalculationTimeoutRef.current) {
        clearTimeout(cargoFigureCalculationTimeoutRef.current);
        cargoFigureCalculationTimeoutRef.current = null;
      }
    };
  }, [blFigure, handleActivityChange, selectedTrip, shipFigure]);

  /**
   * 5. Spara aktivitet till Context och Offline Storage
   */
  const handleSaveActivity = useCallback(
    async (activity) => {
      if (!selectedTrip || !selectedCargo?.cargoId) return;

      const tripId = selectedTrip.tripId || selectedTripId;

      setEditableActivities((prev) => ({ ...prev, [activity]: false }));

      try {
        const success = await updateTripCargo(
          selectedCargo.cargoId,
          {
            activities: { ...cargoActivities },
          },
          { summary: `Updated Statement of Fact activity ${getLabel(activity)}` }
        );

        if (success) {
          addLog({
            user: user?.username || "Unknown",
            activity: "Update SoF",
            details: `Updated: ${getLabel(activity)}`,
            tripId,
            cargoId: selectedCargo.cargoId,
          });
        }
      } catch (error) {
        console.error("Save failed", error);
        setEditableActivities((prev) => ({ ...prev, [activity]: true }));
      }
    },
    [
      addLog,
      cargoActivities,
      selectedCargo?.cargoId,
      selectedTrip,
      selectedTripId,
      updateTripCargo,
      user?.username,
    ]
  );

  const toggleEditable = useCallback((activity) => {
    if (isVoyageReadOnly) return;
    if (editableActivities[activity]) {
      handleSaveActivity(activity);
    } else {
      setEditableActivities((prev) => ({ ...prev, [activity]: true }));
    }
  }, [editableActivities, handleSaveActivity, isVoyageReadOnly]);

  const handleDeleteActivities = useCallback(async () => {
    if (isVoyageReadOnly) return;
    if (!window.confirm(t("confirm_delete"))) return;
    if (!selectedTrip || !selectedCargo?.cargoId) return;

    const tripId = selectedTrip.tripId || selectedTripId;
    const success = await updateTripCargo(
      selectedCargo.cargoId,
      {
        activities: null,
      },
      { summary: "Reset Statement of Fact activities" }
    );

    if (success) {
      setCargoActivities({});
      const resetMap = {};
      Object.keys(activitiesSchema).forEach((key) => {
        resetMap[key] = true;
      });
      setEditableActivities(resetMap);
      addLog({
        user: user?.username || "Unknown",
        activity: "Reset SoF",
        details: "All activities cleared",
        tripId,
        cargoId: selectedCargo.cargoId,
      });
    }
  }, [
    activitiesSchema,
    addLog,
    isVoyageReadOnly,
    selectedCargo?.cargoId,
    selectedTrip,
    selectedTripId,
    t,
    updateTripCargo,
    user?.username,
  ]);

  const setNow = useCallback((activity) => {
    const now = new Date();
    const date = new Intl.DateTimeFormat("sv-SE", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
    const time = new Intl.DateTimeFormat("sv-SE", {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(now);

    handleActivityChange(activity, "date", date);
    handleActivityChange(activity, "time", time);
  }, [handleActivityChange, timezone]);

  const handlePrint = useCallback(() => {
    renderSofV2({
      currentVoyage,
      selectedTripId,
      selectedCargoIndex,
      cargoActivities,
      getLabel,
      companyLogo,
      is24Hour,
      t,
    });
  }, [
    cargoActivities,
    companyLogo,
    currentVoyage,
    is24Hour,
    selectedCargoIndex,
    selectedTripId,
    t,
  ]);

  // Rendering States
  if (loading || !currentVoyage || !selectedTrip) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: 10,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="h6" color="text.secondary">
          Loading SoF Data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="sof-container"
      sx={{ animation: "fadeIn 0.5s ease", p: { xs: 1, md: 3 } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="900" sx={{ letterSpacing: -1 }}>
            {t("title")}
          </Typography>
          <Typography variant="body2" color="primary" fontWeight="700">
            {getVoyageNumber(currentVoyage)} | {selectedTrip.to}
          </Typography>
        </Box>
        <Typography
          variant="subtitle1"
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            px: 2,
            py: 0.5,
            borderRadius: 2,
            fontWeight: 700,
          }}
        >
          {selectedTrip.operationType}:{" "}
          {selectedCargo?.cargo}
        </Typography>
      </Box>

      <VoyageInfoTable
        currentVoyage={currentVoyage}
        selectedTrip={selectedTrip}
        onPrint={handlePrint}
      />

      <Box
        sx={{
          mt: 4,
          p: { xs: 1, md: 3 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <ActivityLogTable
          activities={activitiesSchema}
          cargoActivities={cargoActivities}
          handleActivityChange={handleActivityChange}
          setNow={setNow}
          editableActivities={editableActivities}
          toggleEditable={toggleEditable}
          getLabel={getLabel}
          readOnly={isVoyageReadOnly}
          isActivityEmpty={isActivityEmpty}
          figureDiff={figureDiff}
          percentDiff={percentDiff}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={handleDeleteActivities}
            startIcon={<DeleteIcon />}
            disabled={isVoyageReadOnly}
            sx={{ fontWeight: 700 }}
          >
            {t("deleteactivities")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default StatementOfFact;
